import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { Menu, X, LogOut, Settings, Shield, ChevronRight } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
// Logo served from /public to match index.html preload (avoids duplicate fetch + LCP delay)
const logo = "/logo.webp";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuth } from "@/contexts/auth";
import { trackCta } from "@/lib/analytics";
import { prefetchRoute } from "@/lib/prefetchRoute";

// AuthDialog pulls in Radix Dialog + supabase auth flows — keep it out of the initial chunk
const AuthDialog = lazy(() => import("@/components/AuthDialog"));
// User dropdown menu (Radix DropdownMenu) is also lazy — only signed-in users need it.
const HeaderUserMenu = lazy(() => import("@/components/HeaderUserMenu"));

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const { locale, setLocale, t, localePath } = useLanguage();
  const { user, isAdmin, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { label: t.nav.about, href: "/about" },
    { label: t.nav.achievements, href: "/achievements" },
    { label: t.nav.sales, href: "/sales" },
    { label: t.nav.lectures, href: "/lectures" },
    { label: t.nav.partners, href: "/partners" },
    { label: t.nav.zebbie, href: "/zebbie" },
    { label: t.nav.blog, href: "/blog" },
    { label: t.nav.community, href: "/community" },
  ];

  const isActive = (href: string) => location.pathname === localePath(href);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open — class toggle is composited,
  // doesn't trigger a forced reflow like setting body.style.overflow.
  useEffect(() => {
    const cls = "modal-open";
    if (isOpen) {
      document.body.classList.add(cls);
    } else {
      document.body.classList.remove(cls);
    }
    return () => {
      document.body.classList.remove(cls);
    };
  }, [isOpen]);

  const handleSignOut = async () => {
    await signOut();
    navigate(localePath("/"));
  };

  const closeMobile = useCallback(() => setIsOpen(false), []);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
          <Link to={localePath("/")} className="flex items-center gap-3 z-10">
            <img src={logo} alt="Strachota - Exotic Bird Breeding" width={200} height={42} className="h-9 sm:h-10 md:h-12 w-auto" decoding="sync" fetchPriority="high" />
          </Link>

          {/* Desktop nav (lg+) */}
          <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={localePath(item.href)}
                onMouseEnter={() => prefetchRoute(item.href)}
                onFocus={() => prefetchRoute(item.href)}
                className={`text-sm font-medium transition-colors uppercase tracking-wider whitespace-nowrap ${
                  isActive(item.href) ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {item.label}
              </Link>
            ))}

            <button
              onClick={() => setLocale(locale === "cs" ? "en" : "cs")}
              aria-label={locale === "cs" ? "Switch to English" : "Přepnout do češtiny"}
              className="text-xs font-semibold uppercase tracking-wider border border-border rounded-sm px-2.5 py-1.5 text-muted-foreground hover:text-foreground hover:border-primary transition-colors"
            >
              {locale === "cs" ? "EN" : "CZ"}
            </button>

            {user ? (
              <Suspense
                fallback={
                  <button className="flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-sm text-sm font-semibold uppercase tracking-wider">
                    {locale === "cs" ? "Profil" : "Profile"}
                  </button>
                }
              >
                <HeaderUserMenu
                  email={user.email ?? ""}
                  isAdmin={isAdmin}
                  locale={locale}
                  onAdmin={() => navigate("/admin")}
                  onProfile={() => navigate(localePath("/profile"))}
                  onSignOut={handleSignOut}
                />
              </Suspense>
            ) : (
              <button
                onClick={() => {
                  trackCta({
                    cta_id: "header_signin",
                    cta_location: "header_desktop",
                    cta_label: locale === "cs" ? "Přihlásit se" : "Log In",
                  });
                  setAuthDialogOpen(true);
                }}
                className="bg-gradient-primary text-primary-foreground px-5 py-2 rounded-sm text-sm font-semibold uppercase tracking-wider hover:opacity-90 transition-opacity"
              >
                {locale === "cs" ? "Přihlásit se" : "Log In"}
              </button>
            )}
          </nav>

          {/* Mobile / Tablet hamburger (below lg) — pure CSS icon swap */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-foreground z-10 p-1"
            aria-label={isOpen ? "Close menu" : "Toggle menu"}
          >
            <span className="relative block w-6 h-6">
              <X size={24} className={`absolute inset-0 transition-all duration-150 ${isOpen ? "opacity-100 rotate-0" : "opacity-0 rotate-90"}`} />
              <Menu size={24} className={`absolute inset-0 transition-all duration-150 ${isOpen ? "opacity-0 -rotate-90" : "opacity-100 rotate-0"}`} />
            </span>
          </button>
        </div>
      </header>

      {/* Mobile / Tablet overlay menu — pure CSS transitions */}
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-background/60 backdrop-blur-sm lg:hidden transition-opacity duration-200 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeMobile}
      />

      {/* Slide-in panel */}
      <nav
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-sm bg-background border-l border-border shadow-2xl lg:hidden flex flex-col transition-transform duration-300 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        aria-hidden={!isOpen}
      >
        {/* Panel header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/50">
          <Link to={localePath("/")} onClick={closeMobile} className="flex items-center gap-3">
            <img src={logo} alt="Logo" width={306} height={63} className="h-9 w-auto" />
          </Link>
          <button
            onClick={closeMobile}
            className="p-1 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close menu"
          >
            <X size={22} />
          </button>
        </div>

        {/* Nav links */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="space-y-1">
            {navItems.map((item, i) => (
              <div
                key={item.href}
                className={`transition-all duration-300 ${isOpen ? "opacity-100 translate-x-0" : "opacity-0 translate-x-5"}`}
                style={{ transitionDelay: isOpen ? `${i * 40}ms` : "0ms" }}
              >
                <Link
                  to={localePath(item.href)}
                  onClick={closeMobile}
                  className={`flex items-center justify-between py-3 px-3 rounded-sm text-base font-medium transition-all uppercase tracking-wider ${
                    isActive(item.href)
                      ? "text-primary bg-primary/5"
                      : "text-foreground hover:bg-muted/50"
                  }`}
                >
                  <span>{item.label}</span>
                  <ChevronRight size={16} className={`transition-colors ${isActive(item.href) ? "text-primary" : "text-muted-foreground/40"}`} />
                </Link>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="my-6 border-t border-border/50" />

          {/* Language switch */}
          <button
            onClick={() => { setLocale(locale === "cs" ? "en" : "cs"); closeMobile(); }}
            className={`flex items-center gap-3 py-3 px-3 rounded-sm text-base font-medium text-foreground hover:bg-muted/50 transition-all w-full duration-300 ${isOpen ? "opacity-100 translate-x-0" : "opacity-0 translate-x-5"}`}
            style={{ transitionDelay: isOpen ? `${navItems.length * 40}ms` : "0ms" }}
          >
            <span className="text-lg">{locale === "cs" ? "🇬🇧" : "🇨🇿"}</span>
            <span>{locale === "cs" ? "English" : "Česky"}</span>
          </button>

          {/* User section */}
          {user && (
            <>
              <div className="my-4 border-t border-border/50" />
              <div className="px-3 mb-3">
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>
              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={closeMobile}
                  className="flex items-center gap-3 py-3 px-3 rounded-sm text-base font-medium text-foreground hover:bg-muted/50 transition-all"
                >
                  <Shield size={18} className="text-primary" />
                  {locale === "cs" ? "Administrace" : "Admin Panel"}
                </Link>
              )}
              <Link
                to={localePath("/profile")}
                onClick={closeMobile}
                className="flex items-center gap-3 py-3 px-3 rounded-sm text-base font-medium text-foreground hover:bg-muted/50 transition-all"
              >
                <Settings size={18} className="text-muted-foreground" />
                {locale === "cs" ? "Nastavení" : "Settings"}
              </Link>
              <button
                onClick={() => { handleSignOut(); closeMobile(); }}
                className="flex items-center gap-3 py-3 px-3 rounded-sm text-base font-medium text-foreground hover:bg-muted/50 transition-all w-full"
              >
                <LogOut size={18} className="text-muted-foreground" />
                {locale === "cs" ? "Odhlásit se" : "Log Out"}
              </button>
            </>
          )}
        </div>

        {/* Bottom CTA */}
        {!user && (
          <div className="px-6 py-5 border-t border-border/50">
            <button
              onClick={() => {
                trackCta({
                  cta_id: "header_signin",
                  cta_location: "header_mobile",
                  cta_label: locale === "cs" ? "Přihlásit se" : "Log In",
                });
                setAuthDialogOpen(true);
                closeMobile();
              }}
              className="w-full bg-gradient-primary text-primary-foreground py-3 rounded-sm text-sm font-semibold uppercase tracking-wider hover:opacity-90 transition-opacity text-center"
            >
              {locale === "cs" ? "Přihlásit se" : "Log In"}
            </button>
          </div>
        )}
      </nav>

      {authDialogOpen && (
        <Suspense fallback={null}>
          <AuthDialog open={authDialogOpen} onOpenChange={setAuthDialogOpen} />
        </Suspense>
      )}
    </>
  );
};

export default Header;
